import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { DataGrid } from '../components/common/DataGrid';
import { FilterBar } from '../components/common/FilterBar';
import { Tabs } from '../components/common/Tabs';
import { inventoryApi } from '../api/inventoryApi';
import { PageSyncMeta } from '../components/sync/PageSyncMeta';
import { useSyncStatus } from '../contexts/SyncStatusContext';

// 製品種別フィルタの定義
const productTypeFilters = {
  jukuwaku: { 
    label: '受枠', 
    classcode0: 16, 
    classcode1: null, 
    classcode2: null 
  },
  kago: { 
    label: 'カゴ', 
    classcode0: 19, 
    classcode1: 183, 
    classcode2: null 
  },
  bolt: { 
    label: 'ボルト', 
    classcode0: 20, 
    classcode1: 198, 
    classcode2: 704 
  },
  tekkin: { 
    label: '鉄筋', 
    classcode0: 19, 
    classcode1: [188, 195], 
    classcode2: null 
  },
  jizair: { 
    label: '自在R付属品', 
    classcode0: 19, 
    classcode1: 189, 
    classcode2: null 
  },
  frsokou: { 
    label: 'FR側溝付属品', 
    classcode0: 19, 
    classcode1: 190, 
    classcode2: null 
  },
  dorengu: { 
    label: 'ドレン金具後付', 
    classcode0: 17, 
    classcode1: 175, 
    classcode2: null 
  },
};

const InventoryListPage = () => {
  const [items, setItems] = useState([]);
  const [activeTab, setActiveTab] = useState('all');
  const [selectedProductType, setSelectedProductType] = useState('jukuwaku'); // デフォルトは受枠
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({ keyword: '' });
  const navigate = useNavigate();
  const { refreshSyncStatus } = useSyncStatus();

  useEffect(() => {
    loadItems();
  }, [activeTab, filter, selectedProductType]);

  const loadItems = async () => {
    try {
      setLoading(true);
      const factoryScope = activeTab === 'all' ? 'all' : activeTab;
      const productTypeFilter = productTypeFilters[selectedProductType];
      
      // 鉄筋の場合は配列をカンマ区切り文字列に変換
      const classcode1Value = Array.isArray(productTypeFilter.classcode1) 
        ? productTypeFilter.classcode1.join(',') 
        : productTypeFilter.classcode1;
      
      const data = await inventoryApi.getList(
        factoryScope, 
        filter.keyword,
        productTypeFilter.classcode0,
        classcode1Value,
        productTypeFilter.classcode2
      );
      setItems(data);
    } catch (error) {
      console.error('在庫一覧取得エラー:', error);
      alert('在庫一覧の取得に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  const formatNumber = (value) => {
    if (value === null || value === undefined) return '0';
    return Number(value).toLocaleString();
  };

  const formatStock = (value) => {
    if (value === null || value === undefined) return '0';
    return Number(value).toLocaleString();
  };

  const handleRefresh = async () => {
    await Promise.all([loadItems(), refreshSyncStatus()]);
  };

  const handleCellClick = (row, field) => {
    // 全体タブのみ、倉庫在庫列クリックで遷移
    if (activeTab === 'all') {
      if (field === 'stock_26') {
        navigate(`/inventory/${row.product_disp}?factory=26`);
      } else if (field === 'stock_1') {
        navigate(`/inventory/${row.product_disp}?factory=1`);
      }
    }
  };

  // 全体タブのカラム定義（瑞穂倉庫列を強調）
  const columnsAll = [
    { header: '製品コード', field: 'product_disp' },
    { header: '製品名', field: 'product_name' },
    { header: '寸法', field: 'size_text', render: (row) => row.size_text || '-' },
    { header: '現在庫（全体）', field: 'stock_total', render: (row) => formatNumber(row.stock_total) },
    { 
      header: '瑞穂倉庫現在庫', 
      field: 'stock_26',
      headerStyle: { backgroundColor: '#eff6ff', fontWeight: 'bold' },
      render: (row) => (
        <span 
          className="text-blue-700 hover:text-blue-800 cursor-pointer underline font-bold"
          onClick={(e) => {
            e.stopPropagation();
            handleCellClick(row, 'stock_26');
          }}
        >
          {formatNumber(row.stock_26)}
        </span>
      )
    },
    { 
      header: '資材倉庫現在庫', 
      field: 'stock_1', 
      render: (row) => (
        <span 
          className="text-blue-600 hover:text-blue-800 cursor-pointer underline"
          onClick={(e) => {
            e.stopPropagation();
            handleCellClick(row, 'stock_1');
          }}
        >
          {formatNumber(row.stock_1)}
        </span>
      )
    },
    { header: '標準在庫（全体）', field: 'standard_stock_total', render: (row) => formatStock(row.standard_stock_total) },
    { header: '標準在庫（瑞穂）', field: 'standard_stock_mizuho', render: (row) => formatStock(row.standard_stock_mizuho) },
    { header: '標準在庫（資材）', field: 'standard_stock_shizai', render: (row) => formatStock(row.standard_stock_shizai) },
    { header: '受注残', field: 'so_remaining', render: (row) => formatNumber(row.so_remaining) },
    { header: '発注残', field: 'po_remaining', render: (row) => formatNumber(row.po_remaining) },
  ];

  // 瑞穂倉庫タブのカラム定義
  const columns26 = [
    { header: '製品コード', field: 'product_disp' },
    { header: '製品名', field: 'product_name' },
    { header: '寸法', field: 'size_text', render: (row) => row.size_text || '-' },
    { header: '瑞穂倉庫現在庫', field: 'stock_26', render: (row) => formatNumber(row.stock_26) },
    { header: '標準在庫（瑞穂）', field: 'standard_stock_mizuho', render: (row) => formatStock(row.standard_stock_mizuho) },
    { header: '受注残', field: 'so_remaining', render: (row) => formatNumber(row.so_remaining) },
    { header: '発注残', field: 'po_remaining', render: (row) => formatNumber(row.po_remaining) },
  ];

  // 資材倉庫タブのカラム定義
  const columns1 = [
    { header: '製品コード', field: 'product_disp' },
    { header: '製品名', field: 'product_name' },
    { header: '寸法', field: 'size_text', render: (row) => row.size_text || '-' },
    { header: '資材倉庫現在庫', field: 'stock_1', render: (row) => formatNumber(row.stock_1) },
    { header: '標準在庫（資材）', field: 'standard_stock_shizai', render: (row) => formatStock(row.standard_stock_shizai) },
    { header: '受注残', field: 'so_remaining', render: (row) => formatNumber(row.so_remaining) },
    { header: '発注残', field: 'po_remaining', render: (row) => formatNumber(row.po_remaining) },
  ];

  const getColumns = () => {
    if (activeTab === 'all') return columnsAll;
    if (activeTab === '26') return columns26;
    return columns1;
  };

  return (
    <div className="h-full flex flex-col bg-slate-50">
      <div className="p-6 border-b border-slate-200 bg-white compact:px-4 compact:py-3">
        <div className="page-header">
          <h1 className="page-title">在庫一覧（検索）</h1>
          <div className="page-header-actions">
            <PageSyncMeta groups={['inventory']} />
            <button
              type="button"
              onClick={handleRefresh}
              disabled={loading}
              className="flex items-center gap-2 btn-toolbar bg-slate-700 text-white rounded-md hover:bg-slate-800 transition-colors font-medium text-sm disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <svg
                className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`}
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                />
              </svg>
              更新
            </button>
          </div>
        </div>
      </div>
      <div className="px-6 py-3 border-b border-slate-200 bg-white compact:px-4 compact:py-2">
        <div className="bg-white">
          <form onSubmit={(e) => { e.preventDefault(); loadItems(); }} className="flex flex-wrap gap-3 items-center">
            <input
              type="text"
              value={filter.keyword}
              onChange={(e) => setFilter({ keyword: e.target.value })}
              placeholder="製品コード / 製品名 / 寸法で検索"
              className="flex-1 px-4 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-slate-500 focus:border-slate-500 text-slate-900"
            />
            <button
              type="submit"
              className="btn-toolbar bg-slate-700 text-white rounded-md hover:bg-slate-800 transition-colors font-medium text-sm"
            >
              検索
            </button>
            <button
              type="button"
              onClick={() => { setFilter({ keyword: '' }); loadItems(); }}
              className="btn-toolbar bg-slate-100 text-slate-700 rounded-md hover:bg-slate-200 transition-colors font-medium text-sm"
            >
              クリア
            </button>
          </form>
        </div>
      </div>
      <Tabs
        tabs={[
          { id: 'all', label: '全体' },
          { id: '26', label: '瑞穂倉庫' },
          { id: '1', label: '資材倉庫' },
        ]}
        activeTab={activeTab}
        onTabChange={setActiveTab}
      />
      {/* 製品種別ボタン群 */}
      <div className="px-6 py-3 border-b border-slate-200 bg-white compact:px-4 compact:py-2">
        <div className="filter-toolbar">
          <span className="text-sm font-medium text-slate-700 mr-2">製品種別:</span>
          {Object.entries(productTypeFilters).map(([key, filter]) => (
            <button
              key={key}
              type="button"
              onClick={() => {
                setSelectedProductType(key);
              }}
              disabled={loading}
              className={`px-4 py-2 rounded-md transition-colors font-medium text-sm ${
                selectedProductType === key
                  ? 'bg-slate-700 text-white'
                  : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
              } ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              {filter.label}
            </button>
          ))}
        </div>
      </div>
      <div className="flex-1 overflow-hidden flex flex-col p-6">
        {loading ? (
          <div className="text-center py-8 text-slate-600">読み込み中...</div>
        ) : (
          <div className="flex-1 min-h-0">
            <DataGrid 
              columns={getColumns()} 
              data={items}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default InventoryListPage;
